import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { FlavorsSelection } from "@jti/fe-common";

export type UseFlavorsSelectionControllerArgs = {
  limit: number;
  defaultFlavorsSelection?: FlavorsSelection | null;
  onChange: (selection: FlavorsSelection) => void;
  validate?: boolean;
  hasError?: boolean;
};

export type UseFlavorsSelectionControllerResult = {
  flavorSelection: FlavorsSelection;
  selectedPacksCount: number;
  isLimitReached: boolean;

  temporarySelection: FlavorsSelection;
  temporarySelectedPacksCount: number;
  isTemporaryLimitReached: boolean;

  hasValidationError: boolean;
  checkLimit: (selection: FlavorsSelection) => { limitReached: boolean; selectedPacksCount: number };
  setTemporarySelection: (selection: FlavorsSelection) => void;
  onCollectionChange: (id: string | number, quantity: number) => void;
  onTemporaryCollectionChange: (id: string | number, quantity: number) => void;
  onDrawerApplySelection: (afterApply?: () => void) => void;
};

export function useFlavorsSelectionController({
  limit,
  defaultFlavorsSelection,
  onChange,
  validate = false,
  hasError = false,
}: UseFlavorsSelectionControllerArgs): UseFlavorsSelectionControllerResult {
  const checkLimit = useCallback(
    (selection: FlavorsSelection) => {
      const total = Object.values(selection).reduce((acc, qty) => acc + qty, 0);
      return { limitReached: total >= limit, selectedPacksCount: total };
    },
    [limit]
  );

  const initial = useMemo<FlavorsSelection>(() => defaultFlavorsSelection || {}, [defaultFlavorsSelection]);

  const [flavorSelection, setFlavorSelection] = useState<FlavorsSelection>(initial);
  const flavorSelectionRef = useRef<FlavorsSelection>(initial);
  const [temporarySelection, setTemporarySelectionState] = useState<FlavorsSelection>(initial);

  const { limitReached: isLimitReached, selectedPacksCount } = useMemo(
    () => checkLimit(flavorSelection),
    [checkLimit, flavorSelection]
  );

  const { limitReached: isTemporaryLimitReached, selectedPacksCount: temporarySelectedPacksCount } = useMemo(
    () => checkLimit(temporarySelection),
    [checkLimit, temporarySelection]
  );

  const hasValidationError = useMemo(
    () => validate && (hasError || selectedPacksCount < limit),
    [hasError, limit, selectedPacksCount, validate]
  );

  useEffect(() => {
    onChange(defaultFlavorsSelection || {});
  }, []);

  const onCollectionChange = useCallback(
    (id: string | number, quantity: number) => {
      const key = String(id);
      const updated: FlavorsSelection = { ...flavorSelectionRef.current, [key]: quantity };
      if (quantity === 0) delete updated[key];
      flavorSelectionRef.current = updated;
      setFlavorSelection(updated);
      onChange(updated);
    },
    [onChange]
  );

  const onTemporaryCollectionChange = useCallback((id: string | number, quantity: number) => {
    const key = String(id);
    setTemporarySelectionState((prev) => {
      const updated: FlavorsSelection = { ...prev, [key]: quantity };
      if (quantity === 0) delete updated[key];
      return updated;
    });
  }, []);

  const onDrawerApplySelection = useCallback(
    (afterApply?: () => void) => {
      flavorSelectionRef.current = temporarySelection;
      setFlavorSelection(temporarySelection);
      onChange(temporarySelection);
      afterApply?.();
    },
    [onChange, temporarySelection]
  );

  const setTemporarySelection = useCallback((selection: FlavorsSelection) => {
    setTemporarySelectionState(selection || {});
  }, []);

  return {
    flavorSelection,
    selectedPacksCount,
    isLimitReached,

    temporarySelection,
    temporarySelectedPacksCount,
    isTemporaryLimitReached,

    hasValidationError,
    checkLimit,
    setTemporarySelection,
    onCollectionChange,
    onTemporaryCollectionChange,
    onDrawerApplySelection,
  };
}
